<template>
    <div>
        <button @click="router.go(-1)" type="button" class="btn btn-lg btn-link">
            <i class="fa fa-arrow-left" aria-hidden="true"></i>
            <span>Back</span>
        </button>

        <div style="min-width: 700px; width: 60vw; margin: auto; margin-top: 40px;">
            <h2 class="fg-color-blue">Access tokens</h2>
            <hr /><br />

            <div v-if="succeedUserAccessTokensQueryV1" class="border-box">
                <h4 v-if="accessTokens.length==0">No access tokens generated</h4>

                <div class="row">
                    <div class="col"><strong>Name</strong></div>
                    <div class="col"><strong>Expiration</strong></div>
                    <div class="col"><strong>ID</strong></div>
                    <div class="col"><strong>Secret</strong></div>
                </div>
                <hr />

                <div v-for="token in accessTokens" class="row">
                    <div class="col">
                        <button @click="handleRevokeAccessTokenCommandV1(token)" class="btn btn-sm btn-default" type="button" title="Revoke access token">
                            <i class="fa-solid fa-ban"></i>
                        </button>
                        <span v-if="token.IsExpired || token.IsRevoked" style="text-decoration: line-through;">{{token.Name}}</span>
                        <span v-else>{{token.Name}}</span>
                    </div>
                    <div class="col">{{token.ExpireAt}}</div>
                    <div class="col">{{token.Id}}</div>
                    <div class="col">
                        <div v-if="token.Id==id" class="input-group input-group-sm">
                            <input :value="copied ? 'COPIED!' : accessToken" type="text" class="form-control bg-white" :class="{'text-success': copied}" readonly>
                            <div class="input-group-append">
                                <button @click="handleCopyToClipboard" class="btn btn-outline-secondary" type="button">
                                    <span class="fa fa-copy"></span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
                <hr />
                <button type="button" class="btn btn-secondary" @click="handleGenerateAccessTokenCommandV1">Generate a new token</button>
            </div>
        </div>
    </div>
</template>

<script lang="ts" setup>
    import { computed, onMounted, watch, ref } from 'vue'
    import { guid } from '@common/Guid'
    import { copyToClipboard } from '@common/Common'
    import { useRouter, useOrganizationUnitId } from '@common/composables'
    import { useMyAccessTokensPage } from '@Organizations/AccessTokens/pages-core'

    document.title = "Manage access tokens"

    const router = useRouter()

    const id = ref()
    const secret = ref()
    const copied = ref(false)

    const {
        useCurrentUserQuery,
        useUserAccessTokensQuery,
        useGenerateAccessTokenCommand,
        useRevokeAccessTokenCommand,
    } = useMyAccessTokensPage()

    const accessToken = computed(() => btoa(`${id.value}/${secret.value}`))

    const { modelCurrentUserQueryV1, runCurrentUserQueryV1 } = useCurrentUserQuery()
    const userId = computed(() => modelCurrentUserQueryV1.value?.UserId)
    const organizationUnitId = computed(() => modelCurrentUserQueryV1.value?.OrganizationUnitId)

    const { modelUserAccessTokensQueryV1, runUserAccessTokensQueryV1, succeed: succeedUserAccessTokensQueryV1 } = useUserAccessTokensQuery(userId)
    const accessTokens = computed(() => modelUserAccessTokensQueryV1.value?.AccessTokens || [])

    const { invokeGenerateAccessTokenCommandV1, succeedGenerateAccessTokenCommandV1 } = useGenerateAccessTokenCommand(id, organizationUnitId, userId, secret)

    const { invokeRevokeAccessTokenCommandV1, succeedRevokeAccessTokenCommandV1 } = useRevokeAccessTokenCommand(null, userId)

    onMounted(() => runCurrentUserQueryV1())

    watch(userId, () => runUserAccessTokensQueryV1())

    watch(succeedGenerateAccessTokenCommandV1, () => runUserAccessTokensQueryV1())
    watch(succeedRevokeAccessTokenCommandV1, () => runUserAccessTokensQueryV1())

    function handleGenerateAccessTokenCommandV1() {
        id.value = guid()
        secret.value = `${guid()}${guid()}`.replace(/-/g, '')
        invokeGenerateAccessTokenCommandV1()
    }

    function handleRevokeAccessTokenCommandV1(token) {
        if (confirm(`Do you really want to revoke access token ${token.Name}?`)){
            invokeRevokeAccessTokenCommandV1({id: token.Id})
        }
    }

    function handleCopyToClipboard() {
        copyToClipboard(accessToken.value)
        copied.value = true

        const h = setTimeout(() => {{
            copied.value = false
            clearTimeout(h)
        }}, 1000)
    }
</script>